import { z } from "zod";
import { format as dateFormat } from "date-fns";
import { UTCDate } from "@date-fns/utc";
import { enUS, enGB, fr, frCA, de, es } from "date-fns/locale";

/** Rules offered to authors. `auto` is retained only to read older settings. */
export const validationRules = [
  "auto",
  "none",
  "regex",
  "url",
  "email",
  "phone",
  "integer",
  "number",
  "positive",
  "nonnegative",
  "postal-us",
  "postal-ca",
  "uuid",
  "hex-color",
  "alphanumeric",
] as const;
export const policyReferenceSchema = z
  .object({
    id: z.string().min(1).max(200),
    version: z.number().int().positive(),
    name: z.string().min(1).max(200),
    scope: z.enum(["application", "site", "company", "project", "variable"]),
  })
  .strict();
/** Message keys a validation setting can customise. */
export const validationMessageKeys = [
  "required",
  "minLength",
  "maxLength",
  "min",
  "max",
  "pattern",
  "rule",
  "minSelected",
  "maxSelected",
] as const;
export type ValidationMessageKey = (typeof validationMessageKeys)[number];
/** Rejects patterns that can backtrack badly; numbered groups are allowed. */
export function safePattern(pattern: string): RegExp {
  if (
    pattern.length > 256 ||
    /\\[1-9]|\(\?[=!<]|\([^)]*[+*][^)]*\)[+*{]|\([^)]*\|[^)]*\)[+*{]/.test(
      pattern,
    )
  )
    throw new Error("Pattern uses unsupported or unsafe constructs");
  return new RegExp(pattern, "u");
}
/** Number of capture groups in a (safe) pattern. */
export function patternGroupCount(pattern: string) {
  return (
    new RegExp(`${safePattern(pattern).source}|`, "u").exec("")!.length - 1
  );
}
/**
 * Message templates: `$0` is the entered value, `$1`…`$9` are capture groups
 * from a Custom Regex that rejects matching values, `$$` is a literal `$`.
 */
export function messageTemplateError(
  template: string,
  groups: number,
): string | undefined {
  for (const match of template.matchAll(/\$(\$|\d{1,2}|.?)/g)) {
    const token = match[1];
    if (token === "$") continue;
    if (!/^\d+$/.test(token))
      return "Use $0 for the value, $1–$9 for capture groups or $$ for a dollar sign";
    const n = Number(token);
    if (n > groups)
      return groups
        ? `$${n} refers to a capture group the expression does not have (it has ${groups})`
        : `$${n} needs a capture group; only a Custom Regex that rejects matching values provides groups`;
  }
}
export function renderMessageTemplate(
  template: string,
  value: string,
  match?: RegExpExecArray | null,
) {
  return template.replace(/\$(\$|\d{1,2})/g, (_, token: string) =>
    token === "$"
      ? "$"
      : token === "0"
        ? value
        : (match?.[Number(token)] ?? ""),
  );
}
export const validationSettingsSchema = z
  .object({
    minLength: z.number().int().min(0).max(32768).optional(),
    maxLength: z.number().int().min(0).max(32768).optional(),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    pattern: z.string().max(256).optional(),
    /** `match` (default): the value must match. `reject`: matches fail. */
    patternMode: z.enum(["match", "reject"]).optional(),
    mask: z.string().optional(),
    rule: z.enum(validationRules).optional(),
    /** Checkbox groups and other multi-selection controls. */
    minSelected: z.number().int().min(0).max(10000).optional(),
    maxSelected: z.number().int().min(0).max(10000).optional(),
    messages: z
      .object(
        Object.fromEntries(
          validationMessageKeys.map((key) => [
            key,
            z.string().max(500).optional(),
          ]),
        ) as Record<ValidationMessageKey, z.ZodOptional<z.ZodString>>,
      )
      .strict()
      .optional(),
    preset: policyReferenceSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.min !== undefined && v.max !== undefined && v.min > v.max)
      ctx.addIssue({
        code: "custom",
        message: "Minimum cannot exceed maximum",
        path: ["min"],
      });
    if (
      v.minLength !== undefined &&
      v.maxLength !== undefined &&
      v.minLength > v.maxLength
    )
      ctx.addIssue({
        code: "custom",
        message: "Minimum characters cannot exceed maximum characters",
        path: ["minLength"],
      });
    if (
      v.minSelected !== undefined &&
      v.maxSelected !== undefined &&
      v.minSelected > v.maxSelected
    )
      ctx.addIssue({
        code: "custom",
        message: "Minimum selections cannot exceed maximum selections",
        path: ["minSelected"],
      });
    if (v.rule === "regex" && !v.pattern)
      ctx.addIssue({
        code: "custom",
        message: "Enter a regular expression for the Custom Regex rule",
        path: ["pattern"],
      });
    let groups = 0;
    if (v.pattern)
      try {
        const count = patternGroupCount(v.pattern);
        groups = v.patternMode === "reject" ? count : 0;
      } catch (error) {
        ctx.addIssue({
          code: "custom",
          message: `Regular expression: ${(error as Error).message}`,
          path: ["pattern"],
        });
      }
    for (const key of validationMessageKeys) {
      const template = v.messages?.[key];
      if (!template) continue;
      const error = messageTemplateError(
        template,
        key === "pattern" ? groups : 0,
      );
      if (error)
        ctx.addIssue({
          code: "custom",
          message: `${key} message: ${error}`,
          path: ["messages", key],
        });
    }
  });
/** Custom regex check shared by the engine and editor previews. */
export function patternError(
  validation: ValidationSettings,
  value: string,
): string | undefined {
  if (!validation.pattern) return;
  const regex = safePattern(validation.pattern),
    match = regex.exec(value.slice(0, 32768)),
    failed = validation.patternMode === "reject" ? !!match : !match;
  if (!failed) return;
  return validation.messages?.pattern
    ? renderMessageTemplate(validation.messages.pattern, value, match)
    : validation.patternMode === "reject"
      ? "contains text that is not allowed"
      : "does not match the required format";
}
/** A configured custom message, or the default wording. */
export function validationMessage(
  validation: ValidationSettings,
  key: ValidationMessageKey,
  fallback: string,
  value = "",
) {
  const template = validation.messages?.[key];
  return template ? renderMessageTemplate(template, value) : fallback;
}
const dateLocales = { "en-US": enUS, "en-GB": enGB, fr, "fr-CA": frCA, de, es };
export const supportedDateLocales = Object.keys(dateLocales);
/** Formatting locale value that follows the current user's browser. */
export const browserLocale = "browser";
/** The locale the current runtime should format with. */
export function resolveFormatLocale(locale: string | undefined) {
  if (locale && locale !== browserLocale) return locale;
  const preferred =
    (typeof navigator !== "undefined" && navigator.language) ||
    Intl.DateTimeFormat().resolvedOptions().locale ||
    "en-US";
  try {
    return Intl.NumberFormat.supportedLocalesOf([preferred])[0] || "en-US";
  } catch {
    return "en-US";
  }
}
/** Closest supported date-fns locale for a (resolved) locale tag. */
function dateLocaleFor(locale: string | undefined) {
  const resolved = resolveFormatLocale(locale);
  if (Object.hasOwn(dateLocales, resolved))
    return dateLocales[resolved as keyof typeof dateLocales];
  const language = resolved.split("-")[0];
  const match = Object.keys(dateLocales).find(
    (key) => key === language || key.startsWith(`${language}-`),
  );
  return match ? dateLocales[match as keyof typeof dateLocales] : enUS;
}
/** Common locales offered in the formatting locale picker. */
export const formattingLocales = [
  "en-US", "en-GB", "en-CA", "en-AU", "en-NZ", "en-IE", "en-IN", "en-ZA",
  "fr-FR", "fr-CA", "fr-BE", "fr-CH", "de-DE", "de-AT", "de-CH", "es-ES",
  "es-MX", "es-US", "es-AR", "es-CO", "it-IT", "it-CH", "pt-BR", "pt-PT",
  "nl-NL", "nl-BE", "sv-SE", "nb-NO", "da-DK", "fi-FI", "is-IS", "pl-PL",
  "cs-CZ", "sk-SK", "hu-HU", "ro-RO", "bg-BG", "el-GR", "tr-TR", "ru-RU",
  "uk-UA", "he-IL", "ar-SA", "ar-AE", "hi-IN", "th-TH", "vi-VN", "id-ID",
  "ms-MY", "fil-PH", "zh-CN", "zh-TW", "zh-HK", "ja-JP", "ko-KR",
];
export type NumberPattern = {
  useGrouping: boolean;
  minimumIntegerDigits: number;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
  style: "decimal" | "percent" | "currency";
};
/** Standard decimal skeletons: #,##0.00, 0.##, 000000, 0.0%, ¤#,##0.00. */
export function parseNumberPattern(pattern: string): NumberPattern {
  const match = /^(¤)?(#,##0|0{1,20})(?:\.(0{0,20})(#{0,20}))?(%)?$/.exec(
    pattern,
  );
  if (
    !match ||
    (pattern.includes(".") && !(match[3]?.length || match[4]?.length)) ||
    (match[3]?.length || 0) + (match[4]?.length || 0) > 20 ||
    (match[1] && match[5])
  )
    throw new Error(
      "Use a standard number pattern such as #,##0.00, 0.##, 0000 or 0.0%",
    );
  return {
    useGrouping: match[2].includes(","),
    minimumIntegerDigits: match[2].includes(",") ? 1 : match[2].length,
    minimumFractionDigits: match[3]?.length || 0,
    maximumFractionDigits: (match[3]?.length || 0) + (match[4]?.length || 0),
    style: match[1] ? "currency" : match[5] ? "percent" : "decimal",
  };
}
export function assertDatePattern(pattern: string, locale = "en-US") {
  if (
    !pattern ||
    pattern.length > 128 ||
    !/[yYuUrQqMLwIdDecihHKkmsStaPp]/.test(pattern)
  )
    throw new Error(
      "Enter a date/time pattern, for example yyyy-MM-dd or h:mm a",
    );
  let quoted = false;
  for (let i = 0; i < pattern.length; i++)
    if (pattern[i] === "'") {
      if (pattern[i + 1] === "'") i++;
      else quoted = !quoted;
    }
  if (quoted) throw new Error("Close the quoted text in the date/time pattern");
  if (locale !== browserLocale && !Object.hasOwn(dateLocales, locale))
    throw new Error("Choose a supported date/time locale");
  dateFormat(new UTCDate("2024-02-29T13:05:07Z"), pattern, {
    locale: dateLocaleFor(locale),
  });
}
export const formatSettingsSchema = z
  .object({
    locale: z.string().max(100).default(browserLocale),
    prefix: z.string().max(1000).default(""),
    suffix: z.string().max(1000).default(""),
    uppercase: z.boolean().default(false),
    kind: z.enum(["raw", "text", "date", "number"]).optional(),
    datePattern: z.string().max(128).optional(),
    numberPattern: z.string().max(128).optional(),
    currency: z.string().max(3).optional(),
    casing: z.enum(["unchanged", "lowercase", "title"]).optional(),
    trim: z.boolean().optional(),
    preset: policyReferenceSchema.optional(),
  })
  .superRefine((v, ctx) => {
    try {
      if (v.kind || v.casing) new Intl.NumberFormat(resolveFormatLocale(v.locale));
      if (v.kind === "date") assertDatePattern(v.datePattern || "", v.locale);
      if (v.kind === "number") {
        const options = parseNumberPattern(v.numberPattern || "");
        if (
          options.style === "currency" &&
          !/^[A-Z]{3}$/.test(v.currency || "")
        )
          throw new Error("Choose a three-letter currency code such as USD");
        new Intl.NumberFormat(resolveFormatLocale(v.locale), {
          ...options,
          ...(options.style === "currency" ? { currency: v.currency } : {}),
        });
      }
    } catch (error) {
      ctx.addIssue({
        code: "custom",
        message: (error as Error).message,
        path: [v.kind === "date" ? "datePattern" : "numberPattern"],
      });
    }
  });
export type ValidationSettings = z.infer<typeof validationSettingsSchema>;
export type FormatSettings = z.infer<typeof formatSettingsSchema>;
export type PolicyField = {
  default?: unknown;
  type: string;
  key: string;
  label: string;
  validation: ValidationSettings;
  format: FormatSettings;
};
export function isWebsiteField(
  field: Pick<PolicyField, "type" | "key" | "label">,
) {
  return (
    ["text", "textarea"].includes(field.type) &&
    [field.key, field.label].some((value) =>
      /^(?:website|web[ _-]?address|web[ _-]?url|site[ _-]?url|url|homepage)$|(?:[ _-](?:website|url))$/i.test(
        value.trim(),
      ),
    )
  );
}
export function validWebsite(value: string) {
  if (value !== value.trim() || /\s|[\u0000-\u001f\u007f]/.test(value))
    return false;
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) && !!url.hostname;
  } catch {
    return false;
  }
}
export function strictDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number),
    date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(0, 0, 0, 0);
  return (
    year >= 1 &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}
export function strictTime(value: string) {
  return /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,3})?)?$/.test(value);
}
export function strictDateTime(value: string) {
  const m =
    /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)(Z|[+-]\d{2}:\d{2})?$/.exec(
      value,
    );
  return (
    !!m &&
    strictDate(m[1]) &&
    strictTime(m[2]) &&
    Number.isFinite(Date.parse(value + (m[3] ? "" : "Z")))
  );
}
const decimal = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;
export function numericValue(value: unknown) {
  return typeof value === "number"
    ? value
    : typeof value === "string" && decimal.test(value.trim())
      ? Number(value)
      : NaN;
}
/** Optional empty values are handled by the form's required flag before this. */
export function policyValueError(
  field: PolicyField,
  value: unknown,
): string | undefined {
  // "auto" no longer infers rules from names; only the control type's own
  // semantics (ZIP, URI, phone) still apply. Saved "auto" settings still load.
  const rule = field.validation.rule || "auto",
    s = String(value);
  if (field.format.kind === "number" && !Number.isFinite(numericValue(value)))
    return "enter a finite decimal number for this format";
  if (
    field.format.kind === "date" &&
    !strictDate(s) &&
    !strictDateTime(s) &&
    !strictTime(s)
  )
    return "enter a valid ISO date, date/time or time for this format";
  const effective =
    rule === "auto"
      ? field.type === "zip"
        ? "postal-us"
        : field.type === "uri"
          ? "uri"
          : field.type === "phone"
            ? "phone"
            : "none"
      : rule;
  // Importers retain source tokens as editable defaults. Automatic name inference must
  // preserve that artwork until configured; explicit URL policies always validate it.
  if (
    ["url", "uri", "postal-us", "phone"].includes(effective) &&
    rule === "auto" &&
    value === field.default &&
    (s === `<${field.key}>` || s === `<${field.label}>`)
  )
    return;
  switch (effective) {
    case "uri":
      try {
        const uri = new URL(s);
        return ["http:", "https:", "mailto:", "tel:", "ftp:", "urn:"].includes(
          uri.protocol,
        ) &&
          !/\s/.test(s) &&
          !!uri.pathname
          ? undefined
          : "enter a supported complete URI";
      } catch {
        return "enter a complete URI, such as https://example.com";
      }
    case "url":
      return validWebsite(s) ||
        (rule === "auto" &&
          /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,63}(?::[0-9]+)?(?:[/?#].*)?$/i.test(
            s,
          ) &&
          validWebsite("https://" + s))
        ? undefined
        : "enter a complete HTTP or HTTPS website URL";
    case "email":
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)
        ? undefined
        : "enter a valid email address";
    case "phone":
      return /^\+?[0-9() .-]{7,32}$/.test(s) && s.replace(/\D/g, "").length >= 7
        ? undefined
        : "enter a valid phone number";
    case "number":
    case "integer":
    case "positive":
    case "nonnegative": {
      const n = numericValue(value);
      if (!Number.isFinite(n)) return "enter a finite decimal number";
      if (effective === "integer" && !Number.isSafeInteger(n))
        return "enter a safe whole number";
      if (effective === "positive" && n <= 0)
        return "enter a number greater than zero";
      if (effective === "nonnegative" && n < 0)
        return "enter zero or a positive number";
      if (field.validation.min !== undefined && n < field.validation.min)
        return `minimum ${field.validation.min}`;
      if (field.validation.max !== undefined && n > field.validation.max)
        return `maximum ${field.validation.max}`;
      return;
    }
    case "postal-us":
      return /^\d{5}(?:-\d{4})?$/.test(s)
        ? undefined
        : "enter a five-digit ZIP code or ZIP+4";
    case "postal-ca":
      return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(
        s,
      )
        ? undefined
        : "enter a Canadian postal code";
    case "uuid":
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        s,
      )
        ? undefined
        : "enter a UUID";
    case "hex-color":
      return /^#[0-9a-f]{6}$/i.test(s)
        ? undefined
        : "enter a six-digit hex color";
    case "alphanumeric":
      return /^[\p{L}\p{N}]+$/u.test(s)
        ? undefined
        : "use letters and numbers only";
  }
}
export function formattedPolicyValue(
  field: PolicyField,
  value: unknown,
  plain: string,
) {
  const f = field.format,
    locale = resolveFormatLocale(f.locale);
  let text = plain;
  if (f.kind === "number") {
    const n = numericValue(value);
    if (Number.isFinite(n))
      text = new Intl.NumberFormat(locale, {
        ...parseNumberPattern(f.numberPattern || "0"),
        ...(f.currency ? { currency: f.currency } : {}),
      }).format(n);
  } else if (f.kind === "date") {
    const s = String(value);
    let iso: string | undefined;
    if (strictDate(s)) iso = s + "T00:00:00Z";
    else if (strictTime(s)) iso = "1970-01-01T" + s + "Z";
    else if (strictDateTime(s))
      iso = s + (/(?:Z|[+-]\d{2}:\d{2})$/.test(s) ? "" : "Z");
    if (iso)
      text = dateFormat(new UTCDate(iso), f.datePattern || "yyyy-MM-dd", {
        locale: dateLocaleFor(f.locale),
      });
  }
  if (f.trim) text = text.trim();
  if (f.casing === "lowercase") text = text.toLocaleLowerCase(locale);
  if (f.casing === "title")
    text = text.replace(
      /\p{L}[\p{L}\p{M}]*/gu,
      (word) =>
        word[0].toLocaleUpperCase(locale) +
        word.slice(1).toLocaleLowerCase(locale),
    );
  return text;
}

type ValidationRule = (typeof validationRules)[number];
const textualTypes = [
  "text",
  "textarea",
  "richtext",
  "combobox",
  "email",
  "phone",
  "zip",
  "uri",
];
/** What a control type can be validated by. */
export function validationCapabilities(type: string) {
  const textual = textualTypes.includes(type),
    numeric = type === "number",
    selection = type === "checkbox";
  const rules: ValidationRule[] = textual
    ? [
        "none",
        "regex",
        ...((
          {
            email: ["email"],
            phone: ["phone"],
            zip: ["postal-us", "postal-ca"],
            uri: ["url"],
          } as Record<string, ValidationRule[]>
        )[type] ?? [
          "url",
          "email",
          "phone",
          "postal-us",
          "postal-ca",
          "uuid",
          "hex-color",
          "alphanumeric",
        ]),
      ]
    : numeric
      ? ["none", "integer", "number", "positive", "nonnegative"]
      : ["none"];
  return {
    rules,
    characters: textual,
    range: numeric,
    selections: selection,
    /** Image, map, QR, preset and address controls use their own settings. */
    supported: !["image", "image-list", "map", "qr", "preset", "address"].includes(
      type,
    ),
  };
}
/** Drop settings that do not apply to the control type (e.g. after a type change). */
export function applicableValidation(
  type: string,
  v: ValidationSettings,
): ValidationSettings {
  const can = validationCapabilities(type);
  const result: ValidationSettings = {};
  if (v.preset) result.preset = v.preset;
  if (v.mask && can.characters) result.mask = v.mask;
  if (v.rule && (v.rule === "auto" || can.rules.includes(v.rule)))
    result.rule = v.rule;
  if (can.characters) {
    if (v.minLength !== undefined) result.minLength = v.minLength;
    if (v.maxLength !== undefined) result.maxLength = v.maxLength;
    if (v.pattern) {
      result.pattern = v.pattern;
      if (v.patternMode) result.patternMode = v.patternMode;
    }
  }
  if (can.range) {
    if (v.min !== undefined) result.min = v.min;
    if (v.max !== undefined) result.max = v.max;
  }
  if (can.selections) {
    if (v.minSelected !== undefined) result.minSelected = v.minSelected;
    if (v.maxSelected !== undefined) result.maxSelected = v.maxSelected;
  }
  const keys: ValidationMessageKey[] = [
    "required",
    ...(result.minLength !== undefined ? (["minLength"] as const) : []),
    ...(result.maxLength !== undefined ? (["maxLength"] as const) : []),
    ...(result.min !== undefined ? (["min"] as const) : []),
    ...(result.max !== undefined ? (["max"] as const) : []),
    ...(result.pattern ? (["pattern"] as const) : []),
    ...(result.rule && !["none", "auto", "regex"].includes(result.rule)
      ? (["rule"] as const)
      : []),
    ...(result.minSelected !== undefined ? (["minSelected"] as const) : []),
    ...(result.maxSelected !== undefined ? (["maxSelected"] as const) : []),
  ];
  const messages = Object.fromEntries(
    keys.flatMap((key) =>
      v.messages?.[key] ? [[key, v.messages[key]]] : [],
    ),
  );
  if (Object.keys(messages).length) result.messages = messages;
  return result;
}

/** Formatting kinds offered for a control type (never numbers for images). */
export function formatKindsFor(type: string): FormatSettings["kind"][] {
  if (["image", "image-list", "map", "qr", "preset", "address"].includes(type))
    return [];
  if (type === "number") return ["raw", "number", "text"];
  if (["date", "datetime", "time", "calendar"].includes(type))
    return ["raw", "date", "text"];
  if (["checkbox", "dropdown", "radio", "combobox", "color"].includes(type))
    return ["raw", "text"];
  return ["raw", "text", "date", "number"];
}
/** Whether a (preset) setting fits a control type. */
export function policyFitsType(
  kind: "validation" | "format",
  type: string,
  value: Record<string, unknown>,
) {
  if (kind === "format")
    return formatKindsFor(type).includes(
      (value.kind as FormatSettings["kind"]) || "raw",
    );
  const can = validationCapabilities(type);
  if (!can.supported) return false;
  if (value.rule && value.rule !== "auto" && !can.rules.includes(value.rule as never))
    return false;
  if ((value.maxLength !== undefined || value.minLength !== undefined) && !can.characters)
    return false;
  if ((value.min !== undefined || value.max !== undefined) && !can.range)
    return false;
  return true;
}
